import React from 'react';
import datasetParser from "@Dataset/datasetParser";
import {useTranslation} from "@Translation/hooks/useTranslation";
import Row from "@Containers/components/Row";
import IconLocationMarker from "@Icons/IconLocationMarker";
import StopInfoFragment from "@Pages/stationPage/components/StopInfoFragment";
import IconMapStroke from "@Icons/strokeIcons/IconMapStroke";
import IconMailStroke from "@Icons/strokeIcons/IconMailStroke";
import IconInfo from "@Icons/IconInfo";
import IconOpenLink from "@Icons/IconOpenLink";
import {Colors} from "@Styles/Colors.style";
import IconPhone from "@Icons/IconPhone";
import IconBrandGlobe from "@Icons/IconBrandGlobe";

interface StopInfoProps {
    index?: number
}

const StopInfo = ({index}: StopInfoProps) => {
    const {
        titleId,
        activeAddressDisplay,
        address,
        addressNumber,
        postalCode,
        cityName,
        infoEmail,
        infoPhoneNumberDomestic,
        infoPhoneNumberInternational,
        phoneNumber,
        latitude,
        longitude,
        stopName,
        averageRating,
        numberOfReviews,
        mapText,
    } = datasetParser({className: "stops-info-portal", index})
    const addressLabel = useTranslation("s_page.address")
    const coordinatesLabel = useTranslation("s_page.gps_coordinates")
    const phoneNumberLabel = useTranslation("s_page.phone")
    const infoPhoneNumberDomesticLabel = useTranslation("s_page.info_phone_dom")
    const infoPhoneNumberInternationalLabel = useTranslation("s_page.info_phone_int")
    const infoEmailLabel = useTranslation("contact.email")
    const addressNotAvailable = useTranslation("s_page.not_available")
    const inDoubt = useTranslation("s_page.if_in_doubt")

    const fullAddress = address != ''
        ? (`${address} ${addressNumber} ${postalCode != '' ? `,  ${postalCode} ${cityName ?? ''}` : ''}`
        ) : (
            `${addressNotAvailable} ${inDoubt}`);

    return (
        <>
            <h3 id={titleId} className={'stop-information-field-header'}>
                <Row justify center>
                    <Row>
                        <h3>
                            {stopName}
                        </h3>
                    </Row>
                    {latitude && longitude && (
                        <span className="stop-information-gmap-link">
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${latitude ?? 0}%2C${longitude ?? 0}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {"  "}
                                    <>
                                        Google Maps
                                        <IconOpenLink/>
                                    </>
                                </a>
                        </span>
                    )}
                </Row>
            </h3>
            <div className="stop-information-grid">
                {activeAddressDisplay && (
                    <StopInfoFragment
                        label={addressLabel}
                        icon={<IconLocationMarker fill={Colors.primaryColor}/>}
                    >
                        <span className="stop-information-field-value">{fullAddress}</span>
                    </StopInfoFragment>
                )}
                {latitude != '' && longitude != '' && (
                    <StopInfoFragment
                        label={coordinatesLabel}
                        icon={<IconMapStroke stroke={Colors.primaryColor}/>}
                    >
                        <span className="stop-information-field-value">{`${latitude}, ${longitude}`}</span>
                    </StopInfoFragment>
                )}
                {phoneNumber && (
                    <StopInfoFragment
                        label={phoneNumberLabel}
                        icon={<IconPhone/>}
                    >
                        <span className="stop-information-field-value">{phoneNumber ?? "N/A"}</span>
                    </StopInfoFragment>
                )}
                {infoPhoneNumberDomestic && (
                    <StopInfoFragment
                        label={infoPhoneNumberDomesticLabel}
                        icon={<IconInfo width={16} height={16} fill={Colors.primaryColor}/>}
                    >
                        <span className="stop-information-field-value">{infoPhoneNumberDomestic}</span>
                    </StopInfoFragment>
                )}
                {infoPhoneNumberInternational && (
                    <StopInfoFragment
                        label={infoPhoneNumberInternationalLabel}
                        icon={<IconBrandGlobe/>}
                    >
                        <span className="stop-information-field-value">{infoPhoneNumberInternational}</span>
                    </StopInfoFragment>
                )}
                {infoEmail && (
                    <StopInfoFragment
                        label={infoEmailLabel}
                        icon={<IconMailStroke stroke={Colors.primaryColor}/>}
                    >
                        <span className="stop-information-field-value">{infoEmail}</span>
                    </StopInfoFragment>
                )}
            </div>
        </>
    )
}

export default StopInfo;